import { Link } from "@tanstack/react-router";
import { BarChart3, ImagePlay, Info, MessageCircle, MessagesSquare, Send, ShieldCheck, UserRound, type LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { config } from "@/lib/config";
import { useT } from "@/lib/i18n";
import type { ChannelAccount } from "@/lib/types";
import { useConnectInstagram } from "./hooks";

/**
 * The Business Login scopes Malfak requests (same five, same order as the Meta dashboard Embed URL), each with why.
 * Shown before the person leaves for instagram.com.
 */
export const INSTAGRAM_PERMISSIONS: { scope: "instagram_business_basic" | "instagram_business_manage_comments" | "instagram_business_manage_messages" | "instagram_business_content_publish" | "instagram_business_manage_insights"; icon: LucideIcon }[] = [
  { scope: "instagram_business_basic", icon: UserRound },
  { scope: "instagram_business_manage_messages", icon: MessagesSquare },
  { scope: "instagram_business_manage_comments", icon: MessageCircle },
  { scope: "instagram_business_content_publish", icon: ImagePlay },
  { scope: "instagram_business_manage_insights", icon: BarChart3 },
];

/** The explainer dialog: what is requested and why, then "Continue to Instagram" (starts the OAuth round trip). */
export function ConnectInstagramDialog({ open, onOpenChange, onConnected }: { open: boolean; onOpenChange: (v: boolean) => void; onConnected?: (a: ChannelAccount) => void }) {
  const { t } = useT();
  const connect = useConnectInstagram((a) => {
    onOpenChange(false);
    onConnected?.(a);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t("oauth.explainer.title")}
        description={t("oauth.explainer.intro")}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("oauth.explainer.cancel")}
            </Button>
            <Button
              variant="brand"
              icon={<Send className="flip-rtl" />}
              loading={connect.isPending}
              data-testid="continue-to-instagram"
              onClick={() => connect.mutate({ body: { method: "oauth", channel: "instagram" } }, { onSuccess: (res) => "redirectUrl" in res && config().mock && onOpenChange(false) })}
            >
              {t("oauth.explainer.continue")}
            </Button>
          </>
        }
      >
        <div data-testid="instagram-explainer">
          <h3 className="text-[13px] font-semibold text-ink">{t("oauth.explainer.permissionsTitle")}</h3>
          <ul className="mt-2 space-y-2">
            {INSTAGRAM_PERMISSIONS.map(({ scope, icon: Icon }) => (
              <li key={scope} className="flex items-start gap-3 rounded-xl bg-surface-2 px-3 py-2.5" data-scope={scope}>
                <Icon className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-ink">{t(`oauth.explainer.permissions.${scope}.title`)}</p>
                  <p className="text-[12px] leading-snug text-ink-3">{t(`oauth.explainer.permissions.${scope}.why`)}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 space-y-1.5 text-[12px] leading-snug text-ink-2">
            <p className="flex items-start gap-2">
              <Info className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
              {t("oauth.explainer.importNote")}
            </p>
            <p className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-ok" aria-hidden />
              {t("oauth.explainer.neverPosts")}
            </p>
            <p className="ps-5.5 text-ink-3">
              {t("oauth.explainer.accountsNote")} {t("oauth.explainer.requirement")}{" "}
              <Link to="/privacy" target="_blank" className="font-medium text-accent hover:underline">
                {t("oauth.explainer.privacy")}
              </Link>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A button that opens the explainer (Settings → Channels, onboarding, reconnect). */
export function ConnectInstagramButton({ children, onConnected, ...props }: Omit<ButtonProps, "onClick"> & { children: ReactNode; onConnected?: (a: ChannelAccount) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button {...props} onClick={() => setOpen(true)}>
        {children}
      </Button>
      <ConnectInstagramDialog open={open} onOpenChange={setOpen} onConnected={onConnected} />
    </>
  );
}
